import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { sectionForPath } from './lib/guidetoc'
import { PearlProvider, usePearl } from './lib/pearlchat'
import PearlPanel from './components/PearlPanel'
import { useAuth } from './lib/session'
import { ANY, RosterProvider, useRoster, type TabKey } from './lib/roster'
import { FILTERS, SORTS, type FilterDef } from './lib/filters'
import {
  countActiveRecurringCampaigns,
  countBounces,
  countDueDripTouches,
  countDueTasks,
  countOutboxWaiting,
} from './lib/navcounts'
import ExportMenu from './components/ExportMenu'
import SelectionMenu from './components/SelectionMenu'
import TagMenu from './components/TagMenu'
import ViewsMenu from './components/ViewsMenu'
import { HeadshotProvider } from './lib/headshots'
import Login from './routes/Login'
import Denied from './routes/Denied'
import Workspace from './routes/Workspace'
import Drafts from './routes/Drafts'
import Scheduled from './routes/Scheduled'
import Templates from './routes/Templates'
import Triggers from './routes/Triggers'
import Requests from './routes/Requests'
import Tasks from './routes/Tasks'
import Bounces from './routes/Bounces'
import Guide from './routes/Guide'
import Gaps from './routes/Gaps'
import Email from './routes/Email'
import Outbox from './routes/Outbox'
import Bulk from './routes/Bulk'
import Drips from './routes/Drips'
import Pearl from './components/Pearl'
import SyncStrip from './components/SyncStrip'
import RezenStatus from './components/RezenStatus'
import Boundary from './components/Boundary'
import LeaderboardReminder from './components/LeaderboardReminder'

export default function App() {
  const { status } = useAuth()

  // Nothing of the application shell renders until the allowlist has answered.
  if (status === 'loading') {
    return (
      <div className="gate">
        <div className="gate-card">
          <p className="muted">Checking access…</p>
        </div>
      </div>
    )
  }
  if (status === 'signed-out') return <Login />
  if (status === 'denied') return <Denied />

  return (
    <RosterProvider>
      <HeadshotProvider>
        {/*
          Above the routes on purpose. Pearl's conversation used to be state on
          a routed screen, so leaving the Pearl tab threw it away — which is
          exactly what she does when Pearl has just told her how to do
          something. Held here, it survives every tab click, and the full page
          and the side panel are two views of the same one rather than two
          conversations that do not know about each other.
        */}
        <PearlProvider>
          <Shell />
        </PearlProvider>
      </HeadshotProvider>
    </RosterProvider>
  )
}

/**
 * One dropdown, built from one filter definition. Its options come from the
 * roster rather than from a list written here, so a filter can never offer a
 * choice that matches nobody.
 */
function FilterSelect({ def }: { def: FilterDef }) {
  const r = useRoster()
  return (
    <select
      className="areasel-d"
      value={r.filters[def.key] ?? ANY}
      onChange={(e) => r.setFilter(def.key, e.target.value)}
    >
      <option value={ANY}>{def.all}</option>
      {r.optionsFor(def.key).map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

const AGENT_TABS: { path: string; key: TabKey; label: string }[] = [
  { path: '/', key: 'all', label: 'All' },
  { path: '/capped', key: 'capped', label: 'Capped' },
  { path: '/elite', key: 'elite', label: 'Elite (12 mo)' },
  { path: '/leaders', key: 'leaders', label: 'Team Leaders' },
]

/**
 * What to call each screen when one of them has stopped working.
 *
 * "The Outbox has stopped" is a sentence Renee can act on; "an error occurred"
 * is not. Kept beside the tabs so a new screen without a name here falls back
 * to something honest rather than to a path.
 */
const SCREEN_NAMES: Record<string, string> = {
  '/outreach': 'Outreach',
  '/outbox': 'The Outbox',
  '/bulk': 'Bulk',
  '/drips': 'Drips',
  '/scheduled': 'Scheduled Sends',
  '/templates': 'Templates',
  '/triggers': 'Milestone triggers',
  '/missing': "What's Missing",
  '/tasks': 'Tasks',
  '/bounces': 'Bounces',
  // Reached from the toolbar button rather than a tab, which makes naming it
  // matter more, not less — she has no tab to point at when describing it.
  '/email': 'The email composer',
  '/requests': 'Requests',
  '/pearl': 'Pearl',
  '/guide': 'The Guide',
}

/**
 * The name of the window the Guide opens in.
 *
 * A NAME rather than `_blank`, and that is the whole design. `_blank` spawns a
 * fresh tab on every click — press Guide five times and there are five of them.
 * A named target re-uses the same window, so there is exactly ONE Guide, it can
 * be parked on a second screen, and "What is this screen?" re-aims that window
 * at the right section instead of opening another.
 *
 * Chris, 2026-08-19: he wants it on a second screen while he works through the
 * thing it describes. A document you read beside the work is not a screen you
 * navigate to.
 */
const GUIDE_WINDOW = 'agentcare-guide'

/**
 * WHY THERE IS NO `rel="noopener"` ON THE TWO LINKS THAT USE THE NAME ABOVE.
 *
 * `noopener` tells the browser the new page must not be able to reach back
 * through `window.opener` — and to guarantee that, the specification has it
 * IGNORE THE TARGET NAME and create a fresh browsing context every time. So
 * `target="agentcare-guide" rel="noopener"` behaves exactly like `_blank`: five
 * clicks, five Guide windows, and the one-window design silently gone. It looks
 * correct in the markup and fails only in use, which is the worst way for a
 * thing to be wrong.
 *
 * Dropping it is safe HERE and only here: the target is our own page, on our
 * own origin, and `window.opener` pointing at the dashboard from the dashboard
 * gives away nothing. `noopener` earns its place on links to somewhere we do
 * not control. There are none of those in this app.
 */

const TOOLBAR_FILTERS = FILTERS.filter((f) => f.placement === 'toolbar')
const PANEL_FILTERS = FILTERS.filter((f) => f.placement === 'panel')

function Shell() {
  const { appUser, signOut } = useAuth()
  const r = useRoster()
  const nav = useNavigate()
  const [showFilters, setShowFilters] = useState(false)

  /**
   * The roster toolbar — agent count, area/MLS/tag filters, "include departed" —
   * belongs to the agent lists and nowhere else. Anywhere else it is a row of
   * controls that change nothing on screen, which reads as broken.
   *
   * This was fixed once for Pearl alone, which left the same row sitting
   * uselessly over Outreach, Scheduled Sends and Templates. Deriving it from
   * AGENT_TABS instead of naming the exceptions means a new tab is opted out by
   * default — the safe direction for a mistake to go.
   */
  // Trailing slash stripped first: React Router matches "/capped/" to the
  // /capped route, so without this the list renders while the toolbar hides.
  const location = useLocation()
  const path = location.pathname.replace(/\/+$/, '') || '/'

  /**
   * The three nav badges added 2026-08-22 — Tasks, Bounces, Scheduled Sends —
   * "does this tab need me to look at it, without opening it." See
   * `lib/navcounts.ts` for what each one actually counts and why.
   *
   * `null` means "not known yet" (still loading, or the read failed) — the
   * badge simply does not render rather than show a wrong or flickering
   * zero. Refetched on every navigation (`path` as a dependency), and a
   * badge going stale right after she clears the thing it was warning about
   * is worse than asking again each time she moves screens — true even for
   * Drips and Outbox below (2026-08-23), which reuse the tabs' own real
   * "what's due" logic rather than a head-count, and so cost a little more
   * than the original three.
   */
  const [dueTaskCount, setDueTaskCount] = useState<number | null>(null)
  const [bounceCount, setBounceCount] = useState<number | null>(null)
  const [activeScheduledCount, setActiveScheduledCount] = useState<number | null>(null)
  const [dueDripCount, setDueDripCount] = useState<number | null>(null)
  const [outboxWaitingCount, setOutboxWaitingCount] = useState<number | null>(null)
  useEffect(() => {
    let cancelled = false
    void countDueTasks().then((r) => { if (!cancelled) setDueTaskCount(r.error ? null : r.count) })
    void countBounces().then((r) => { if (!cancelled) setBounceCount(r.error ? null : r.count) })
    void countActiveRecurringCampaigns().then((r) => {
      if (!cancelled) setActiveScheduledCount(r.error ? null : r.count)
    })
    void countDueDripTouches().then((r) => { if (!cancelled) setDueDripCount(r.error ? null : r.count) })
    void countOutboxWaiting().then((r) => { if (!cancelled) setOutboxWaitingCount(r.error ? null : r.count) })
    return () => {
      cancelled = true
    }
  }, [path])

  /**
   * What clears a crash card. The router's `key` changes on every navigation
   * INCLUDING a click on the tab you are already standing on — which is the
   * first thing most people press when a screen is stuck, and which the
   * pathname alone did not notice.
   */
  const resetKey = `${path}#${location.key}`
  const onAgentList = AGENT_TABS.some((t) => t.path === path)
  // Export acts on the list actually on screen, so it needs to know which tab
  // that is — the same value the route below passes to Workspace.
  const currentTab = AGENT_TABS.find((t) => t.path === path)?.key ?? 'all'
  const currentScreen = onAgentList ? 'The agent list' : (SCREEN_NAMES[path] ?? 'This screen')

  /**
   * `--chrome`: how much of the window the app's own furniture is using, right
   * now, measured rather than assumed.
   *
   * This replaces `calc(100vh - 132px)` in `styles.css` — a hard constant
   * standing for the header plus the tabs, which had been named as a trap in
   * three reviews because ANY new band across the top would push Pearl's
   * message box below the fold without a word. The measurement cannot go stale
   * that way.
   *
   * It is the LIVE viewport position of the bottom of the tab row, not a fixed
   * height, because nothing at the top of this app is sticky. Scroll down and
   * the tabs leave the screen — so the side panel, which is fixed, has to grow
   * to fill the space they vacated or it hangs off the bottom with a gap above
   * it. Clamped at zero for when they have gone entirely.
   *
   * Re-measured on the path as well as on scroll and resize: the tab row wraps
   * to two lines on a narrow window, and which screen is showing changes
   * nothing about it — but arriving at a screen after scrolling elsewhere would
   * otherwise inherit whatever the last measurement happened to be.
   *
   * `useLayoutEffect`, not `useEffect`, so the value is set before the browser
   * paints. In an ordinary effect the first painted frame always used the
   * fallback, which on any window where the tab row wraps is a visible jump.
   */
  const tabs = useRef<HTMLDivElement>(null)
  const shellBody = useRef<HTMLDivElement>(null)
  /**
   * The sync-status strip and the reZEN line, wrapped in one element purely so
   * there is something whose OWN box size to hand `ResizeObserver` below.
   *
   * Both load their data after mount and change height on their own schedule —
   * `SyncStrip` renders nothing until its first `sync_runs` read resolves, then
   * grows by a line; `said`, `detail` and the extension state can each add or
   * drop a line later; `RezenStatus` follows the same shape. None of that is a
   * `path` change, a window resize, or a scroll, and neither strip is `tabs` or
   * `.shell-body` itself, so without observing THIS element directly nothing
   * notices — `--chrome` and `--shell-top` would go on reporting the position
   * these strips pushed everything down FROM, not the position they pushed
   * everything down TO, until she next scrolls, resizes, or navigates.
   */
  const topStrip = useRef<HTMLDivElement>(null)
  /**
   * `--shell-top`: the live top edge of `.shell-body`, the same idea as
   * `--chrome` and measured alongside it (2026-08-23), for Pearl's sidebar
   * specifically. `--chrome` stops at the tab row and does not know whether
   * the toolbar below it is showing — right for `.pearl-page` (the old
   * full-page view, only ever reached on `/pearl`, where the toolbar is
   * always hidden) but wrong for the sidebar, which can be open on a roster
   * tab with the toolbar visible. Measuring `.shell-body` itself, whatever
   * is or is not above it, is what keeps the panel's height correct either
   * way without asking it to know the difference.
   */
  useLayoutEffect(() => {
    let frame: number | null = null
    const measure = () => {
      frame = null
      const el = tabs.current
      if (el) {
        const bottom = Math.max(0, Math.round(el.getBoundingClientRect().bottom))
        document.documentElement.style.setProperty('--chrome', `${bottom}px`)
      }
      const body = shellBody.current
      if (body) {
        const top = Math.max(0, Math.round(body.getBoundingClientRect().top))
        document.documentElement.style.setProperty('--shell-top', `${top}px`)
      }
    }
    const onChange = () => {
      if (frame !== null) return
      frame = window.requestAnimationFrame(measure)
    }
    measure()
    window.addEventListener('scroll', onChange, { passive: true })
    window.addEventListener('resize', onChange)
    /**
     * The tab row changes height without a resize, a scroll or a navigation:
     * the four count badges appear and disappear with the mailing-list scope,
     * and on a narrow window that is enough to un-wrap a line. Watching the
     * element itself is the only thing that covers the case this measurement
     * replaced a hard constant to avoid. The sync-status strip above the
     * header (2026-08-23) shows and hides the same way between screens and
     * between its own tones, which is exactly the same case for
     * `--shell-top` — so it is watched too, not just the tabs.
     */
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(onChange) : null
    if (ro && tabs.current) ro.observe(tabs.current)
    if (ro && shellBody.current) ro.observe(shellBody.current)
    if (ro && topStrip.current) ro.observe(topStrip.current)
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame)
      ro?.disconnect()
      window.removeEventListener('scroll', onChange)
      window.removeEventListener('resize', onChange)
    }
  }, [path])

  /**
   * The guide section that explains whatever is on screen.
   *
   * Null on the Guide itself — a link from the guide to the guide is a button
   * that appears to do nothing — and null on any address without an entry,
   * which is the safe direction for a mistake: a missing link is a small loss,
   * a link that lands on the wrong paragraph teaches her not to trust it.
   */
  const helpSection = path === '/guide' ? null : sectionForPath(path)

  /**
   * The panel is closed on arrival at the Pearl tab, where the full page takes
   * over the same conversation.
   *
   * This has to live in `Shell`, which is always mounted — the first attempt
   * put it inside `AskPearl`, which is not rendered on `/pearl` at all, so the
   * effect could never run on the one path it was written for. Left open, the
   * panel silently reappeared the moment she navigated away again: a panel she
   * never asked for, over a screen she had just arrived at.
   */
  const { panelOpen, closePanel } = usePearl()
  useEffect(() => {
    if (path === '/pearl' && panelOpen) closePanel()
  }, [path, panelOpen, closePanel])

  return (
    <>
      {/*
        THE VERY TOP OF THE PAGE, ABOVE THE HEADER — moved here 2026-08-23.
        Used to sit between the tab row and the toolbar, which put it
        directly in the middle of the search/filter block: the header's own
        search box right above it, the toolbar's filters right below it.
        Chris's word for that was that it "breaks up the search features."
        This is still the first thing on the first screen she opens in the
        morning, which is the one moment "the roster has not updated since
        Thursday" is worth anything — it just no longer sits inside the
        block it has nothing to do with. Still not carried into the Outbox
        or the composer, where it would be furniture inside a week.
      */}
      {/*
        The strip reads a table and talks to a browser extension, and it sits
        above everything. A throw in it used to take the roster with it.
      */}
      {/*
        `compact`, because this is one line and a page-sized crash card here
        pushed the whole page down. `quiet`, because a crash in it used to
        print that card at the top of every other screen for the rest of
        the session.
      */}
      {/*
        `topStrip` wraps both — see its own comment by the ref declaration —
        so a `ResizeObserver` has one element whose own box size actually
        changes when either strip appears, disappears, or grows a line, and
        `--chrome`/`--shell-top` get re-measured when that happens instead of
        only on the next scroll, resize or navigation.
      */}
      <div ref={topStrip}>
        <Boundary where="The update line" resetKey={resetKey} quiet={!onAgentList} compact>
          <SyncStrip visible={onAgentList} />
        </Boundary>
        {/*
          Its own Boundary, not folded into the one above — same reasoning
          `PearlPanel`'s separate boundary documents further down this file: a
          throw in the reZEN line must not take the roster's own update line
          down with it, and the reverse.
        */}
        <Boundary where="The reZEN update line" resetKey={resetKey} quiet={!onAgentList} compact>
          <RezenStatus visible={onAgentList} />
        </Boundary>
      </div>

      {/*
        The monthly "Check Leaderboard" pop-up for Renee (2026-10-12) — see
        `lib/reminder-schedule.ts`. Fixed-position overlay, so it adds nothing
        to the page height the comments above are careful about. Its own quiet
        Boundary: a throw in it must never take the dashboard down with it.
      */}
      <Boundary where="The leaderboard reminder" resetKey={resetKey} quiet compact>
        <LeaderboardReminder />
      </Boundary>

      <header>
        <h1>Agent Care</h1>
        <input
          id="search"
          placeholder="Search name, team, area, email, phone, address, licence, MLS, tag…"
          value={r.search}
          onChange={(e) => r.setSearch(e.target.value)}
        />
        <div className="notesbar">
          {/*
            IN THE TITLE BAR, NOT IN A ROW OF ITS OWN.

            `.pearl` is sized `calc(100vh - 132px)` — a hard constant assuming
            the header and the tabs and nothing else. Any new band above the
            routes pushes Pearl's message box below the fold, on every screen,
            for one link. This row already exists, so it costs nothing.

            Words rather than a `?` icon, because a question mark is a guess
            and this is the control somebody reaches for when they are already
            unsure.
          */}
          {helpSection && (
            /*
              A NEW TAB, not a navigation, and that is the whole value of it.
              She presses this because she does not understand the screen in
              front of her — sending her away from that screen to read about it
              is the one thing it must not do. In its own tab she reads the
              instructions beside the thing they describe, and can leave it open
              all day.

              It aims the SAME named window the Guide tab uses, so there is
              only ever one Guide open and this re-points it at the section for
              whatever screen she is on. See `GUIDE_WINDOW` above for why there
              is deliberately no `rel="noopener"` on it.
            */
            <a className="btn ghost sm" href={`/guide#${helpSection}`} target={GUIDE_WINDOW}>
              What is this screen?
            </a>
          )}
          {/*
            NOT on the Pearl tab. Two comments elsewhere assert that the panel
            and the full page are never both mounted, and until this line
            nothing enforced it: pressing Ask Pearl while standing on /pearl put
            two views of the same conversation on screen, both bound to the same
            draft — so typing appeared in both boxes, and the caret jumped from
            one to the other every time a reply landed.
          */}
          {path !== '/pearl' && <AskPearl />}
          <span>
            {appUser?.full_name || appUser?.email}
            {appUser?.role === 'owner' && <span className="badge own">OWNER</span>}
          </span>
          <button className="btn ghost sm" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </header>

      <div className="tabs" ref={tabs}>
        {AGENT_TABS.map((t) => (
          <NavLink key={t.path} to={t.path} end={t.path === '/'} className="tab">
            {t.label}
            {/*
              The mailing-list scope replaces the question rather than narrowing
              it, so while it is on the tabs all answer the same number — and
              "Capped 273" is a sentence that is not true. Drop the counts
              rather than print four wrong ones; the toolbar says what is going
              on, and Show everyone brings them back.
            */}
            {!r.selectedOnly && <span className="n">{r.counts[t.key]}</span>}
          </NavLink>
        ))}
        {/*
          Outreach is deliberately not in this bar — Chris, 2026-08-23: "Hide
          it now." It was a read-only cross-check of the milestone engine's
          drafts against what the old dashboard actually sent, for the
          parallel-run staging comparison; see
          `hide-outreach-tab-flagged-2026-08-23.md` for the full reasoning.
          The route below is left in place rather than deleted — nothing here
          claims the comparison is done for good, only that it does not need
          its own tab right now. `/outreach` still works if it is ever needed
          again; it is just no longer one click away.
        */}
        <NavLink to="/outbox" className="tab">
          Outbox
          {/* Everything the tab itself would call waiting — reviewed/milestone
              drafts plus generated-but-unsent scheduled campaigns. See
              `navcounts.ts`'s `countOutboxWaiting`. */}
          {outboxWaitingCount != null && outboxWaitingCount > 0 && (
            <span className="n">{outboxWaitingCount}</span>
          )}
        </NavLink>
        {/*
          Bulk sits next to the Outbox because they are the two halves of the
          same idea: the Outbox is emails waiting for her to read one at a time,
          this is the one she reads once. It is the ONLY place a bulk send can
          be started — nothing on a schedule can start one.
        */}
        <NavLink to="/bulk" className="tab">
          Bulk
        </NavLink>
        {/*
          Next to Scheduled Sends because they are the two clock-driven screens,
          and a drip is the one that decides *by itself* that somebody is due an
          email today. It still cannot send one: approving a due touch writes
          drafts into the Outbox for her to read and send, like a milestone.
        */}
        <NavLink to="/drips" className="tab">
          Drips
          {/* Agents with a touch due right now, summed across every active
              campaign — see `navcounts.ts`'s `countDueDripTouches`. */}
          {dueDripCount != null && dueDripCount > 0 && <span className="n">{dueDripCount}</span>}
        </NavLink>
        <NavLink to="/scheduled" className="tab">
          Scheduled Sends
          {/* How many recurring campaigns are live right now — see
              `lib/navcounts.ts`. Null (unknown/failed) renders nothing,
              same rule the Tasks/Bounces badges below follow. */}
          {activeScheduledCount != null && activeScheduledCount > 0 && (
            <span className="n">{activeScheduledCount}</span>
          )}
        </NavLink>
        <NavLink to="/templates" className="tab">
          Templates
        </NavLink>
        <NavLink to="/triggers" className="tab">
          Triggers
        </NavLink>
        <NavLink to="/missing" className="tab">
          What's Missing
        </NavLink>
        <NavLink to="/tasks" className="tab">
          Tasks
          {/* Open tasks due today or overdue — not every open task. */}
          {dueTaskCount != null && dueTaskCount > 0 && <span className="n">{dueTaskCount}</span>}
        </NavLink>
        <NavLink to="/bounces" className="tab">
          Bounces
          {/* Every bounce on record — there is no "addressed" flag other
              than deleting it, so this count IS "how many still need
              attention". */}
          {bounceCount != null && bounceCount > 0 && <span className="n">{bounceCount}</span>}
        </NavLink>
        <NavLink to="/requests" className="tab">
          Requests
        </NavLink>
        <NavLink to="/pearl" className="tab">
          Pearl
        </NavLink>
        {/*
          An anchor, not a NavLink — this one does not change the screen you are
          on. It opens (or re-uses) the Guide window.

          It keeps its place and its look in the tab row deliberately: Guide is
          where anybody would look for the guide, and moving it somewhere else
          to signal that it behaves differently would cost more than the
          surprise it saves. It never takes the `.active` state, because it is
          never the screen you are standing on.
        */}
        <a className="tab" href="/guide" target={GUIDE_WINDOW}>
          Guide
        </a>
      </div>

      <div className="toolbar" hidden={!onAgentList}>
        {/*
          While the mailing-list scope is on, the dropdowns and the search box
          are ignored — see `Scope.selectedOnly`. Saying so on the toolbar, with
          the way out next to it, is the difference between "772 agents are
          missing" and "I am looking at my list".
        */}
        {r.selectedOnly ? (
          <>
            <span className="cnt">Mailing list · {r.selectedCount}</span>
            <button className="btn ghost sm" onClick={() => r.setSelectedOnly(false)}>
              Show everyone
            </button>
          </>
        ) : (
          <span className="cnt">
            {/*
              BUG, flagged during the 2026-08-23 walkthrough: this used to
              read `r.counts.all` unconditionally, so every tab — Capped,
              Elite, Team Leaders — showed the full roster total ("778
              agents") instead of that tab's own filtered count. `currentTab`
              already exists a few lines up for exactly this purpose (it
              already drives SelectionMenu/ExportMenu/TagMenu/ViewsMenu
              below) — this was the one reader of `r.counts` that had not
              been switched over to it.
            */}
            {r.counts[currentTab]} {r.counts[currentTab] === 1 ? 'agent' : 'agents'}
          </span>
        )}
        {TOOLBAR_FILTERS.map((f) => (
          <FilterSelect key={f.key} def={f} />
        ))}
        <button
          className={`btn sm ${r.activeFilters > 0 ? '' : 'ghost'}`}
          onClick={() => setShowFilters((v) => !v)}
        >
          More filters{r.activeFilters > 0 ? ` · ${r.activeFilters}` : ''}
        </button>
        {/*
          The way out of a filter, next to the filters.

          "Clear all filters" lived only inside the More filters panel — but the
          three dropdowns beside it set filters without that panel ever being
          opened, so the only way to undo them was a button behind a control
          there was no reason to press. Somebody narrows the roster to eleven
          people, cannot work out how to get back, and reloads the page.

          **Always rendered, greyed out when there is nothing to clear.** The
          first attempt only showed it when a filter was set, which is tidier and
          worse: an absent control cannot be learned, so the first time she
          needed it she would be looking for something she had never seen. A
          greyed-out one teaches itself the moment a filter goes on.

          **`r.activeFilters` alone is not enough, found 2026-08-22.**
          `activeFilters` only counts the dropdown-style filters
          (area/MLS/tags/More filters) — it has never looked at the search
          box. Typing a name with nothing else set left this disabled, so a
          click did nothing: `clearFilters()` itself has always correctly
          cleared the search too, but a disabled button's `onClick` never
          fires, so that correct logic was simply never reached in the single
          most common case (type a name, hit Clear). `r.search` has to be
          part of the same test.
        */}
        <button
          className="btn sm ghost"
          onClick={() => r.clearFilters()}
          disabled={r.activeFilters === 0 && !r.search}
        >
          Clear filters
        </button>
        {/*
          The Mailing list button appears only once something is ticked.

          It is a basket, and an empty basket is not a thing to look at. On a
          single-person send — search a name, press Email — it was a second
          control offering to do what the first one was already doing, which is
          exactly how it came to feel redundant.

          Nothing is hidden by this. The tick boxes are always in the roster, so
          the feature is discovered by ticking, not by finding the menu; and the
          moment one is ticked the button is there with its count. Same rule the
          Email button already follows, which shows a number only when there is
          a selection.

          **`undoSelection` is the second half of the condition, and it is not
          optional.** "Put them back" renders inside this menu. On a count alone,
          clearing the list would drop the count to zero, unmount the menu, and
          take the undo with it — at the one moment she wants it, having just
          emptied a list she may have spent two passes of filtering to build.
          The menu already has copy for the zero state, so it reads correctly
          while it is standing there for the undo alone.
        */}
        {(r.selectedCount > 0 || r.undoSelection) && <SelectionMenu tab={currentTab} />}
        <ExportMenu tab={currentTab} />
        <TagMenu tab={currentTab} />
        {/*
          Beside Export and Tag because it acts on the same thing they do —
          whatever the filters have narrowed the roster to, or the mailing list
          if one is being built. It only ever opens the composer; nothing here
          sends.

          The tab goes in the URL, the same value Export and Tag are handed.
          Without it the composer asked the roster for "all" and got 785 while
          the screen behind it showed 58 team leaders — Export and Tag were
          right about the same list and only this one was wrong, which is the
          hardest kind of wrong to notice.

          One deliberate difference from Export: when a mailing list exists, the
          composer takes the WHOLE list and ignores the tab, because a mailing
          list is built by ticking *across* filters — Charleston, then a team
          that spans four regions — and narrowing it back down by the tab she
          happens to be standing on would undo the point of it. The button says
          the number, so what it will do is on screen before it is pressed.
        */}
        <button className="btn sm" onClick={() => nav(`/email?tab=${currentTab}`)}>
          Email{r.selectedCount > 0 ? ` ${r.selectedCount}` : ''}
        </button>
        <ViewsMenu tab={currentTab} />
        <label className="chk">
          <input
            type="checkbox"
            checked={r.includeDeparted}
            onChange={(e) => r.setIncludeDeparted(e.target.checked)}
          />
          Include departed
        </label>
        <span className="spacer" />
        <span className="hint">
          Test data · the existing dashboard is still the record · nothing sends without you
        </span>
      </div>

      {/*
        Everything in here narrows the list the same way the three dropdowns
        above do — they are one list in `filters.ts`, split only by where they
        are shown. A new filter appears here without this file changing.
      */}
      <div className="fpanel" hidden={!onAgentList || !showFilters}>
        {PANEL_FILTERS.map((f) => (
          <label className="f" key={f.key}>
            <span>{f.label}</span>
            <FilterSelect def={f} />
          </label>
        ))}
        <label className="f">
          <span>Sort by</span>
          <select
            className="areasel-d"
            value={r.sortKey}
            onChange={(e) => r.setSortKey(e.target.value)}
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <div className="fclear">
          {/* Same fix as the toolbar's own "Clear filters" above, and for the
              same reason — this one is a second copy of the identical bug. */}
          <button
            className="btn ghost sm"
            onClick={() => r.clearFilters()}
            disabled={r.activeFilters === 0 && !r.search}
          >
            Clear all filters
          </button>
        </div>
      </div>

      {/*
        Pearl as a true sidebar, decided 2026-08-21: it resizes the work area
        rather than floating over it. `.shell-main` is the flex sibling that
        actually shrinks — `min-width: 0` on it is load-bearing, not
        decoration, because without it a flex item refuses to shrink below
        its content's intrinsic width and the roster's own two-pane grid
        (`.wrap`, 330px + 1fr) would push `.pearlpanel` off the edge of the
        window instead of making room for it.
      */}
      <div className="shell-body" ref={shellBody}>
        <div className="shell-main">
          {/*
            `resetKey`, not `key`. Both clear a crash when she navigates, but a
            `key` also remounts the children on every ordinary tab click — and
            `AgentPanel` holds a half-typed note in component state, so checking
            the Capped tab mid-sentence threw the note away with no warning.
          */}
          <Boundary where={currentScreen} resetKey={resetKey}>
            <Routes>
            {AGENT_TABS.map((t) => (
              <Route key={t.path} path={t.path} element={<Workspace tab={t.key} />} />
            ))}
            <Route path="/outreach" element={<Drafts />} />
            <Route path="/scheduled" element={<Scheduled />} />
            <Route path="/templates" element={<Templates />} />
            <Route path="/triggers" element={<Triggers />} />
            <Route path="/email" element={<Email />} />
            <Route path="/outbox" element={<Outbox />} />
            <Route path="/bulk" element={<Bulk />} />
            <Route path="/drips" element={<Drips />} />
            <Route path="/missing" element={<Gaps />} />
            <Route path="/tasks" element={<Tasks />} />
            <Route path="/bounces" element={<Bounces />} />
            <Route path="/requests" element={<Requests />} />
            <Route path="/pearl" element={<Pearl />} />
            <Route path="/guide" element={<Guide />} />
            {/*
              Send an unrecognised address to the All list rather than rendering it
              there. Rendering it left the URL saying one thing and the screen
              another, and the toolbar rule above — which keys off the path — had no
              way to know a list was on screen.
            */}
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Boundary>
        </div>

        {/*
          Beside the routed content, not outside it in the DOM — it has to be
          a flex sibling of `.shell-main` for the resize to happen at all.
          Still its own Boundary, for the reason the longer comment used to
          say here: Pearl is what she reaches for when a screen has gone
          wrong, so a throw in `PearlView` (it calls `countFor` and
          `describeView` during render for every turn carrying an offer, and
          a restored offer with a malformed filter list would throw there)
          must not blank the whole app past this boundary and the one around
          the routes above. Same reasoning, `quiet compact` for the same
          reason too: a page-sized crash card here would blow the sidebar out
          to the width of an error message.

          Never on the Pearl tab, where the full page is already showing the
          same conversation — belt as well as the braces on the button above,
          because the panel can also be left open while she navigates ONTO
          /pearl.
        */}
        {path !== '/pearl' && (
          <Boundary where="Pearl" resetKey={resetKey} quiet compact>
            <PearlPanel />
          </Boundary>
        )}
      </div>
    </>
  )
}

/**
 * The way in to Pearl from any screen.
 *
 * Beside "What is this screen?" because they are the two halves of the same
 * offer: one explains what she is looking at, the other answers questions about
 * it. Both live in the title bar, which already exists — a new row across the
 * top would push Pearl's own message box below the fold.
 */
function AskPearl() {
  const { panelOpen, openPanel, closePanel } = usePearl()
  return (
    <button className="btn ghost sm" onClick={panelOpen ? closePanel : openPanel}>
      {panelOpen ? 'Hide Pearl' : 'Ask Pearl'}
    </button>
  )
}
